'use client';

import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { safeLocalStorage } from '@/utils/browser-storage';
import { EmarsysWebSDK } from 'index.d';
import Image from '@/components/Image';
import { useEffect, useState } from 'react';
import { useTranslations } from '@/i18n/use-translations';

export default function EmarsysDialog() {
  const t = useTranslations('components.EmarsysDialog');
  // 状态管理
  const [show, setShow] = useState(false);
  const [fieldId] = useState(705);
  const [permissionGrantedRejectedWebPush, setPermissionGrantedRejectedWebPush] = useState(false);
  const [permissionGrantedWebPush, setPermissionGrantedWebPush] = useState(false);
  const [isSubscribedStatus, setIsSubscribedStatus] = useState(false);
  const [customerId, setCustomerId] = useState('');

  // 从store获取token
  const token = useStore((state) => state.token);
  // 公共函数：处理订阅状态检查和用户登录/登出
  const handleSubscriptionStatus = (
    sdk: EmarsysWebSDK,
    userId: string,
    context: 'token-change' | 'init' = 'init',
  ) => {
    sdk.isSubscribed().then((isSubscribed: boolean) => {
      logger.log(
        `[WebEmarsysSDK:handleSubscriptionStatus] isSubscribed: ${isSubscribed} (${context})`,
      );

      // 更新订阅状态（仅在初始化时更新）
      if (context === 'init') {
        setIsSubscribedStatus(isSubscribed);
      }

      if (isSubscribed && userId) {
        const contactInfo = {
          fieldId: Number(fieldId),
          fieldValue: userId,
        };
        sdk.login(contactInfo).then(function (res: boolean) {
          logger.log(`[WebEmarsysSDK:handleSubscriptionStatus] Login Result: ${res} (${context})`);
          if (res) {
            safeLocalStorage.setItem('emarsysLogin', 'login');
          }
        });
      } else if (isSubscribed && !userId) {
        sdk.logout().then(function (res: boolean) {
          logger.log(`[WebEmarsysSDK:handleSubscriptionStatus] Logout Result: ${res} (${context})`);
          if (res) {
            safeLocalStorage.removeItem('emarsysLogin');
          }
        });
      }

      setTimeout(() => {
        logger.log(
          '[WebEmarsysSDK:handleSubscriptionStatus]',
          permissionGrantedWebPush,
          isSubscribedStatus,
          permissionGrantedRejectedWebPush,
        );
        if (permissionGrantedWebPush === true && isSubscribedStatus === false) {
          sdk.subscribe();
          logger.log(
            '[WebEmarsysSDK:handleSubscriptionStatus] Resubscribe: WebEmarsysSdk.subscribe()',
          );
        }
        if (!permissionGrantedRejectedWebPush) {
          setShow(true);
        }
      }, 5000);
    });
  };

  // 初始化customerId
  useEffect(() => {
    const userId = safeLocalStorage.getItem('zl-userId') || '';
    setCustomerId(userId);
  }, []);

  // 监听token变化
  useEffect(() => {
    if (!token) return;

    const currentCustomerId = safeLocalStorage.getItem('zl-userId') || '';
    setCustomerId(currentCustomerId);

    if (window.WebEmarsysSdk && 'isSubscribed' in window.WebEmarsysSdk) {
      const sdk = window.WebEmarsysSdk;
      sdk.push(function () {
        handleSubscriptionStatus(sdk, currentCustomerId, 'token-change');
      });
    }
  }, [
    token,
    fieldId,
    permissionGrantedWebPush,
    isSubscribedStatus,
    permissionGrantedRejectedWebPush,
  ]);

  // 初始化逻辑
  useEffect(() => {
    logger.log('[WebEmarsysSDK:useEffect] emarsys init');
    initEmarsys();

    // 监听通知权限变化
    if ('permissions' in navigator) {
      navigator.permissions
        .query({ name: 'notifications' as PermissionName })
        .then((notificationPerm) => {
          notificationPerm.onchange = function () {
            const userId = safeLocalStorage.getItem('zl-userId') || '';
            setCustomerId(userId);
            logger.log('[WebEmarsysSDK:useEffect] notificationPerm');

            // call generalSubscribe() only after first webpush subscription
            if (!safeLocalStorage.getItem('first-webpush-subscription')) {
              generalSubscribe();
            } else {
              safeLocalStorage.setItem('first-webpush-subscription', 'yes');
            }
          };
        });
    }
  }, []);

  // 初始化Emarsys SDK
  const initEmarsys = () => {
    logger.log('[WebEmarsysSDK:initEmarsys] emarsys init');

    if (window.WebEmarsysSdk) {
      const sdk = window.WebEmarsysSdk;
      sdk.push([
        'onReady',
        function () {
          sdk.push([
            'onPermissionGranted',
            function () {
              setPermissionGrantedRejectedWebPush(true);
              setPermissionGrantedWebPush(true);
              if (!safeLocalStorage.getItem('emarsysSubscribe')) {
                logger.log('[WebEmarsysSDK:initEmarsys] existing subscriber: web push migration');
                sdk.subscribe();
                safeLocalStorage.setItem('emarsysSubscribe', 'subscribe');
              }
            },
          ]);

          // 用户拒绝授权
          sdk.push([
            'onPermissionDenied',
            function () {
              logger.log('[WebEmarsysSDK:initEmarsys] EVENT: onPermissionDenied');
              safeLocalStorage.setItem('emarsysSubscribe', 'denied');
              setPermissionGrantedRejectedWebPush(true);

              // 取消订阅
              sdk.logout().then(function (res: boolean) {
                logger.log(`[WebEmarsysSDK:initEmarsys] onUnsubscribe: logout result: ${res}`);
                if (res) {
                  safeLocalStorage.removeItem('emarsysLogin');
                }
              });
            },
          ]);

          // 使用公共函数处理订阅状态
          handleSubscriptionStatus(sdk, customerId, 'init');
        },
      ]);
    }
  };

  // 处理Later按钮点击
  const handleLaterClick = () => {
    setShow(false);
  };

  // 处理订阅
  const generalSubscribe = () => {
    if (!window.WebEmarsysSdk || !('subscribe' in window.WebEmarsysSdk)) return;

    const sdk = window.WebEmarsysSdk;
    sdk.push(() => {
      setShow(false);
      sdk.subscribe();
      sdk.push([
        'onSubscribe',
        function () {
          safeLocalStorage.setItem('emarsysSubscribe', 'subscribe');
          if (customerId) {
            // for user in login status
            sdk.push(() => {
              sdk
                .isSubscribed()
                .then(() => {
                  const contactInfo = {
                    fieldId: Number(fieldId),
                    fieldValue: customerId,
                  };
                  sdk.login(contactInfo).then((resA: boolean) => {
                    logger.log(
                      `[WebEmarsysSDK:generalSubscribe] generalSubscribe: login result: ${resA}`,
                    );
                    if (resA) {
                      safeLocalStorage.setItem('emarsysLogin', 'login');
                    }
                  });
                })
                .catch((err: Error) => {
                  logger.error('[WebEmarsysSDK:generalSubscribe] subscribed error', err);
                });
            });
          }
        },
      ]);
    });
  };

  if (!show) {
    return null;
  }

  return (
    <div className="w-full flex justify-center items-center">
      <div className="fixed z-[3000] bottom-0 lg:top-0 my-0 mx-auto p-24 w-[100%] lg:w-[500px] h-[178px] rounded-md bg-white box-border shadow-[0_0_12px_rgba(0,0,0,0.72)]">
        <div className="h-80 flex justify-between items-center">
          <Image
            src="https://s3.zeelool.com/admin/shop/home/8b5d0a0dc64fc6fdaaf06c9f561ce345.jpg"
            alt="Zeelool"
            className="w-80 h-80"
            width={512}
            height={512}
          />
          <span
            className="ml-16 leading-[1.3] text-[17px]"
            dangerouslySetInnerHTML={{ __html: t('description') }}
          />
        </div>

        <div className="w-full flex justify-end items-center">
          <button
            onClick={handleLaterClick}
            className="px-24 py-12 font-16 border-none rounded-md cursor-pointer text-base text-[#0078d1] bg-white shadow-none"
          >
            {t('buttons.later')}
          </button>
          <button
            onClick={generalSubscribe}
            className="px-24 py-12 font-16  border-none rounded-md cursor-pointer text-base text-white bg-[#0078d1]"
          >
            {t('buttons.subscribe')}
          </button>
        </div>
      </div>
    </div>
  );
}
